import { createSelector } from 'reselect'

import { sortBy } from 'lib/array/arrayUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { seatMapFlightId } from 'lib/flights/flightSummaryHelpers'

export interface FlightSeatsReviewSegment {
  flightId: string
  /** Order flight item this segment belongs to — seats are only editable on the item in the cart. */
  flightItemId: string
  departureAirport: string
  arrivalAirport: string
  departingDate: string
  provider?: string
  travellers: Array<App.FlightTraveller>
  flight: App.LegFlightWithAddons
  /**
   * Every flight on the leg this segment belongs to. Inclusions are recorded against the leg and
   * copied onto each segment below, so anything that reasons about who operates the allowance has
   * to judge the whole leg — a per-segment carrier test would attribute leg-wide wording to one
   * segment of a mixed-carrier leg.
   */
  legFlights: Array<App.LegFlight>
}

export function getFlightSeatsReviewSegments(
  legs: Array<App.Leg> | undefined,
  flightItemId: string,
  travellers: Array<App.FlightTraveller>,
): Array<FlightSeatsReviewSegment> {
  if (!legs?.length) { return [] }

  return legs.flatMap((leg) => (
    (leg.flights ?? []).flatMap((flight) => {
      const flightId = seatMapFlightId(flight)
      if (!flightId) { return [] }

      return [{
        flightId,
        flightItemId,
        travellers,
        departureAirport: flight.departureAirport,
        arrivalAirport: flight.arrivalAirport,
        departingDate: flight.departingDate,
        provider: leg.provider,
        legFlights: leg.flights ?? [],
        // Baggage, meals and purchased add-ons are recorded against the leg, not the segment, so
        // they are copied down here the same way the My Escapes flight ingress does it. Without
        // this every allowance reads as "not included". Drop it once the API puts them on flights.
        flight: {
          ...flight,
          cabin: leg.cabin,
          inclusions: leg.inclusions,
          travellerFareDetails: leg.travellerFareDetails,
          addons: leg.addons,
        } as App.LegFlightWithAddons,
      }]
    })
  ))
}

/**
 * Every flight in the booking, in departure order — not only the item the seat change is on. The
 * review lists them all as tabs so the customer sees the whole trip; which of them can take a seat
 * change is decided per segment by the component.
 */
export function getAllFlightSeatsReviewSegments(
  flightItems: Array<{ id: string }> | undefined,
  flightDetails: App.OrdersState['flightDetails'],
): Array<FlightSeatsReviewSegment> {
  if (!flightItems?.length) { return [] }

  const segments = flightItems.flatMap((item) => {
    const details = flightDetails[item.id]?.flight
    if (!details) { return [] }
    return getFlightSeatsReviewSegments(
      details.bookingJourney?.legs,
      item.id,
      details.travellers ?? [],
    )
  })

  return sortBy(segments, segment => segment.departingDate, 'asc')
}

type FlightSeatsState = NonNullable<App.CheckoutState['flightSeats']>

/**
 * What the customer pays for a post-booking seat change.
 *
 * Same-price and dearer seats are charged at full list price, not the difference from the held
 * seat. Cheaper seats are charged at $0 — there is no refund of what was already paid. See
 * `paidSeatPricesForReservation` in svc-flights for where that charge is resolved.
 */
export function getFlightSeatsTotalPrice(flightSeats: FlightSeatsState): number {
  return flightSeats.selections.reduce((total, selection) => total + selection.price, 0)
}

export const getFlightSeatsTotal = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.flightSeats,
  (postPurchaseMode, flightSeats): number => {
    if (postPurchaseMode !== 'flight-seats' || !flightSeats) { return 0 }
    return getFlightSeatsTotalPrice(flightSeats)
  },
)

export function buildFlightSeatAddonInputs(
  flightSeats: FlightSeatsState,
): Array<App.FlightSeatAddonInput> {
  return flightSeats.selections.map((selection) => ({
    type: 'SEATS',
    payload: {
      travellerId: selection.travellerId,
      flightId: selection.flightId,
      seatNumber: selection.seatNumber,
    },
  }))
}
